import { z } from "zod";

import type {
  Filter,
  FilterOptions,
  Order,
  PredecisionOutcome,
} from "./api/contract.ts";

/**
 * El buscador y los filtros de la cola, EN LA URL.
 *
 * Todo lo que decide qué se ve en la cola viaja en los search params: el
 * texto del buscador (`q`) y cada filtro con su nombre. Así una búsqueda se
 * comparte con un link y recargar no la pierde. Este archivo es el único que
 * convierte esos params en Criteria (`filtersOf`): la pantalla no arma
 * filtros por su cuenta.
 *
 * Los params tienen nombres legibles (`chainId=101`, `hasPriceAlerts=true`) en
 * vez de Criteria en JSON, para que el link se entienda al leerlo.
 */

/**
 * «En revisión manual»: esperando a una persona. Es lo de partida. Las
 * pestañas la reparten según lo que propone el sistema.
 */
export const IN_REVIEW = "en-revision";
/** Sin filtro de estado ni de paso. */
export const ANY_STAGE = "todas";

/**
 * Las pestañas de la cola (`contrato-predecision.md`, §10), por lo que
 * propone el sistema. La primera es la de partida.
 */
export const TABS = [
  { value: "pre-aprobadas", outcome: "pre_approved", label: "Pre-aprobadas" },
  { value: "pre-rechazadas", outcome: "pre_rejected", label: "Pre-rechazadas" },
  { value: "por-digitar", outcome: "needs_review", label: "Por digitar" },
] as const satisfies ReadonlyArray<{
  value: string;
  outcome: PredecisionOutcome;
  label: string;
}>;

export type TabValue = (typeof TABS)[number]["value"];

export const DEFAULT_TAB: TabValue = "pre-aprobadas";

/** La pestaña de la URL, con la de partida si no hay. */
export const tabOf = (search: DigitationSearch) =>
  TABS.find((tab) => tab.value === search.tab) ?? TABS[0];

/**
 * El router lee como número lo que parece un número (`?q=2026`, `?f=746868`)
 * y como booleano `true`.
 *
 * La factura abierta y el buscador se dejan como lleguen --número o texto--
 * y se leen con `textOf`: si el esquema los devolviera como texto, el router
 * los escribiría entre comillas para que no parezcan números, y la URL
 * quedaría `?f=%22746868%22`. Lo demás sí se devuelve a lo que es.
 */
const loose = z.union([z.string(), z.number()]).optional().catch(undefined);

/** Lo que vino como número o texto, como texto. */
export const textOf = (
  value: string | number | undefined,
): string | undefined => (value === undefined ? undefined : String(value));

const text = z
  .union([z.string(), z.number()])
  .transform((value) => String(value))
  .optional()
  .catch(undefined);

const id = z
  .union([z.number(), z.string()])
  .transform((value) => Number(value))
  .refine((value) => Number.isInteger(value) && value > 0)
  .optional()
  .catch(undefined);

const flag = z.boolean().optional().catch(undefined);

export const digitationSearchSchema = z.object({
  /** La factura abierta, por su número en lala4. Se lee con `textOf`. */
  f: loose,
  /** Lo escrito en el buscador, ya confirmado con Enter. Se lee con `textOf`. */
  q: loose,
  /** La pestaña; sin ella, «Pre-aprobadas». */
  tab: z
    .enum(["pre-aprobadas", "pre-rechazadas", "por-digitar"])
    .optional()
    .catch(undefined),
  /**
   * El estado o el paso: `en-revision` (de partida), `todas`,
   * `status:<estado>` o `step:<paso>`.
   */
  stage: text,
  photoType: text,
  chainId: id,
  pharmacyId: id,
  period: text,
  /** La bolsa de un año entero: la abre «Todo 2026» del Resumen. */
  year: id,
  hasUnknownLines: flag,
  hasPriceAlerts: flag,
  /** Solo las que nadie tiene tomadas. */
  unclaimed: flag,
  /** El modo «por llegada»: las tres clasificaciones juntas, por subida. */
  order: flag,
});

export type DigitationSearch = z.infer<typeof digitationSearchSchema>;

/** Los filtros de la URL, como Criteria (§1). El buscador va aparte, en `q`. */
export const filtersOf = (search: DigitationSearch): Filter[] => {
  const filters: Filter[] = [];
  const stage = search.stage ?? IN_REVIEW;
  if (stage === IN_REVIEW) {
    filters.push(
      { field: "status", operator: "eq", value: "waiting" },
      { field: "stepKey", operator: "eq", value: "human-review" },
    );
  } else if (stage.startsWith("status:")) {
    filters.push({ field: "status", operator: "eq", value: stage.slice(7) });
  } else if (stage.startsWith("step:")) {
    filters.push({ field: "stepKey", operator: "eq", value: stage.slice(5) });
  }
  if (search.photoType !== undefined) {
    filters.push({
      field: "photoType",
      operator: "eq",
      value: search.photoType,
    });
  }
  if (search.chainId !== undefined) {
    filters.push({ field: "chainId", operator: "eq", value: search.chainId });
  }
  if (search.pharmacyId !== undefined) {
    filters.push({
      field: "pharmacyId",
      operator: "eq",
      value: search.pharmacyId,
    });
  }
  if (search.period !== undefined) {
    filters.push({ field: "period", operator: "eq", value: search.period });
  }
  if (search.year !== undefined) {
    filters.push({ field: "year", operator: "eq", value: search.year });
  }
  if (search.hasUnknownLines === true) {
    filters.push({ field: "hasUnknownLines", operator: "eq", value: true });
  }
  if (search.hasPriceAlerts === true) {
    filters.push({ field: "hasPriceAlerts", operator: "eq", value: true });
  }
  if (search.unclaimed === true) {
    filters.push({ field: "claimed", operator: "eq", value: false });
  }
  if (search.order !== true) {
    filters.push({
      field: "predecision",
      operator: "eq",
      value: tabOf(search).outcome,
    });
  }
  return filters;
};

/** El orden que pide el modo «por llegada»: ascendente por subida. */
export const orderOf = (
  search: DigitationSearch,
): readonly Order[] | undefined =>
  search.order === true ? [{ by: "uploadedAt", direction: "asc" }] : undefined;

/** Lo que se le pide al API: el buscador y los filtros, sin la factura abierta. */
export const criteriaOf = (
  search: DigitationSearch,
): {
  readonly q?: string;
  readonly filters: readonly Filter[];
  readonly order?: readonly Order[];
} => {
  const filters = filtersOf(search);
  const q = textOf(search.q)?.trim() ?? "";
  const order = orderOf(search);
  return {
    ...(q === "" ? {} : { q }),
    filters,
    ...(order === undefined ? {} : { order }),
  };
};

/** Cuántos filtros hay puestos además de lo de partida, para decirlo. */
export const activeFilterCount = (search: DigitationSearch): number =>
  [
    search.stage !== undefined && search.stage !== IN_REVIEW,
    search.photoType,
    search.chainId,
    search.pharmacyId,
    search.period,
    search.year,
    search.hasUnknownLines,
    search.hasPriceAlerts,
    search.unclaimed,
  ].filter((value) => value !== undefined && value !== false).length;

/** Lo escrito se suma a lo que ya había: el buscador es uno solo. */
export const appendQuery = (q: string | undefined, typed: string): string =>
  [q ?? "", typed]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" ");

/**
 * Quitar un chip es quitar del buscador el pedazo que lo produjo (`raw`), una
 * sola vez. Lo demás queda como estaba.
 */
export const removeRaw = (q: string | undefined, raw: string): string => {
  const source = q ?? "";
  const index = source.indexOf(raw);
  if (index < 0) return source.trim();
  return `${source.slice(0, index)} ${source.slice(index + raw.length)}`
    .replace(/\s+/g, " ")
    .trim();
};

/** Las opciones del select «Estado o paso», con «En revisión manual» primero. */
export const stageOptions = (
  options: FilterOptions | undefined,
): ReadonlyArray<{ value: string; label: string; count: number | null }> => [
  { value: IN_REVIEW, label: "En revisión manual", count: null },
  { value: ANY_STAGE, label: "Todas", count: null },
  ...(options?.statuses ?? []).map((option) => ({
    value: `status:${option.value}`,
    label: option.label,
    count: option.count,
  })),
  ...(options?.steps ?? []).map((option) => ({
    value: `step:${option.value}`,
    label: `En «${option.label}»`,
    count: option.count,
  })),
];
